import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { QuizResult } from '../types';

const ResultsPage: React.FC = () => {
  const navigate = useNavigate();
  const [results, setResults] = useState<QuizResult[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get quiz results from session storage
    const storedResults = sessionStorage.getItem('quizResults');
    
    if (!storedResults) {
      // If no results are found, redirect to home page
      navigate('/');
      return;
    }
    
    setResults(JSON.parse(storedResults));
    setLoading(false);
  }, [navigate]);

  const handleTryAgain = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Calculate score
  const correctAnswers = results.filter(result => result.isCorrect).length;
  const totalQuestions = results.length;
  const scorePercentage = Math.round((correctAnswers / totalQuestions) * 100);

  return (
    <Box>
      <Typography 
        variant="h4" 
        component="h1" 
        gutterBottom
        sx={{ textAlign: 'center', mb: 4 }}
      >
        Quiz Results
      </Typography>
      
      <Card 
        elevation={3} 
        sx={{ 
          p: 3, 
          mb: 4, 
          borderRadius: 2,
          backgroundColor: 'primary.light',
          color: 'white'
        }}
      >
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid sx={{ textAlign: 'center' }}>
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <CircularProgress 
                  variant="determinate" 
                  value={scorePercentage} 
                  size={120}
                  thickness={5}
                  sx={{ 
                    color: scorePercentage >= 70 ? 'success.light' : 'warning.light',
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    borderRadius: '50%'
                  }}
                />
                <Box
                  sx={{
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="h4" component="div" color="white">
                    {scorePercentage}%
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid >
              <Typography variant="h5" gutterBottom>
                Your Score
              </Typography>
              <Typography variant="h6">
                {correctAnswers} correct out of {totalQuestions} questions
              </Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>
                {scorePercentage >= 90 ? 'Excellent! You\'re a French verb master!' :
                 scorePercentage >= 70 ? 'Great job! Keep practicing!' :
                 scorePercentage >= 50 ? 'Good effort! More practice will help.' :
                 'Keep practicing! You\'ll improve with time.'}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          Question Review
        </Typography>
        
        <Divider sx={{ mb: 2 }} />
        
        <List>
          {results.map((result, index) => (
            <React.Fragment key={index}>
              <ListItem alignItems="flex-start">
                <Box sx={{ display: 'flex', width: '100%' }}>
                  {result.isCorrect ? (
                    <CheckCircleOutlineIcon color="success" sx={{ mr: 2 }} />
                  ) : (
                    <CancelOutlinedIcon color="error" sx={{ mr: 2 }} />
                  )}
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1">
                        Question {index + 1}: Conjugate "{result.question.verb.infinitive}" in {result.question.tense.name} for "{result.question.pronoun}"
                      </Typography>
                    }
                    secondary={
                      <React.Fragment>
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.primary"
                        >
                          Your answer: 
                        </Typography>
                        {' '}
                        <Typography
                          component="span"
                          variant="body2"
                          color={result.isCorrect ? 'success.main' : 'error.main'}
                          sx={{ fontWeight: 'bold' }}
                        >
                          {result.selectedAnswer}
                        </Typography>
                        {!result.isCorrect && (
                          <React.Fragment>
                            <br />
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.primary"
                            >
                              Correct answer: 
                            </Typography>
                            {' '}
                            <Typography
                              component="span"
                              variant="body2"
                              color="success.main"
                              sx={{ fontWeight: 'bold' }}
                            >
                              {result.question.correctAnswer}
                            </Typography>
                          </React.Fragment>
                        )}
                      </React.Fragment>
                    }
                  />
                </Box>
              </ListItem>
              {index < results.length - 1 && <Divider component="li" />}
            </React.Fragment>
          ))}
        </List>
        
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleTryAgain}
            size="large"
            sx={{ px: 4 }}
          >
            Practice Again
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ResultsPage;
